/* eslint-disable */
/**
 * Permission Mixin (MGU Approach)
 *
 * This mixin provides a simple permission checking system using string-based permissions.
 * Permissions are stored as strings in format: "MODULE.PERMISSION" (e.g., "ROOMS.CREATE")
 *
 * Usage in component:
 * import permissionMixin from '@/mixins/permissionMixin'
 *
 * export default {
 *   mixins: [permissionMixin],
 *   // ... component code
 * }
 *
 * Then use in template:
 * v-if="can('ROOMS.CREATE')"
 * v-if="can('USERS.UPDATE')"
 * v-if="can('AGENCIES.DELETE')"
 *
 * Available permission types:
 * - MODULE.VIEW          - Can view list/index page
 * - MODULE.SINGLE_VIEW   - Can view single record details
 * - MODULE.CREATE        - Can create new records
 * - MODULE.UPDATE        - Can edit existing records
 * - MODULE.DELETE        - Can delete records
 * - MODULE.MASTERS       - Can access master data
 */

import { mapState } from 'vuex'

export default {
  computed: {
    ...mapState('auth', ['user', 'permissions']),

    /**
     * Get the current user's role name
     * @returns {string} Role name (e.g., 'ADMIN', 'SUPER ADMIN', 'USER')
     */
    userRole() {
      if (!this.user || !this.user.roles || !this.user.roles.length) {
        return null
      }
      return this.user.roles[0].name
    },
  },

  methods: {
    /**
     * Check if the current user has a specific permission
     *
     * ADMIN and SUPER ADMIN always return true (bypass permission checks)
     *
     * @param {string} permission - Permission string in format "MODULE.PERMISSION" (e.g., "ROOMS.CREATE")
     * @returns {boolean} True if user has the permission, false otherwise
     *
     * @example
     * // In template
     * <v-btn v-if="can('ROOMS.CREATE')">Create Room</v-btn>
     * <v-btn v-if="can('USERS.UPDATE')">Edit User</v-btn>
     * <v-btn v-if="can('AGENCIES.DELETE')">Delete Agency</v-btn>
     *
     * // In script methods
     * if (this.can('ROOMS.CREATE')) {
     *   this.$router.push('/rooms/create')
     * }
     */
    can(permission) {
      if (!permission) return false

      // SUPER ADMIN and ADMIN bypass all permission checks
      if (this.userRole === 'SUPER ADMIN' || this.userRole === 'ADMIN') {
        return true
      }

      // Check if permission string exists in permissions array
      return this.permissions && this.permissions.includes(permission)
    },

    /**
     * Check if user CANNOT perform an action
     * @param {string} permission - Permission string (e.g., "ROOMS.CREATE")
     * @returns {boolean}
     */
    cannot(permission) {
      return !this.can(permission)
    },

    /**
     * Check if user has ANY of the given permissions
     * @param {Array<string>} permissionArray - Array of permission strings
     * @returns {boolean}
     *
     * @example
     * // Show button if user can either create or update rooms
     * <v-btn v-if="canAny(['ROOMS.CREATE', 'ROOMS.UPDATE'])">Manage Rooms</v-btn>
     */
    canAny(permissionArray) {
      return permissionArray.some(permission => this.can(permission))
    },

    /**
     * Check if user has ALL of the given permissions
     * @param {Array<string>} permissionArray - Array of permission strings
     * @returns {boolean}
     *
     * @example
     * // Show button only if user has both permissions
     * <v-btn v-if="canAll(['ROOMS.UPDATE', 'ROOMS.DELETE'])">Full Management</v-btn>
     */
    canAll(permissionArray) {
      return permissionArray.every(permission => this.can(permission))
    },
  },
}
